"use client";

import { useState, useEffect } from "react";
import { useFormDraft } from "@/app/lib/hooks/useFormDraft";

function ChatIcon({ className = "w-6 h-6" }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
    </svg>
  );
}

type FeedbackType = "suggestion" | "bug" | "question" | "other";

type WidgetDraft = {
  feedbackType: string;
  message: string;
  email: string;
};

const feedbackTypes: { value: FeedbackType; label: string; emoji: string }[] = [
  { value: "suggestion", label: "Suggestion", emoji: "💡" },
  { value: "bug", label: "Bug Report", emoji: "🐛" },
  { value: "question", label: "Question", emoji: "❓" },
  { value: "other", label: "Other", emoji: "💬" },
];

export default function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const { values: draftValues, updateField: updateDraft, saveNow, clearDraft, hasDraft, lastSaved } = useFormDraft<WidgetDraft>(
    "feedback_widget",
    { feedbackType: "suggestion", message: "", email: "" }
  );
  const [feedbackType, setFeedbackType] = useState<FeedbackType>((draftValues.feedbackType as FeedbackType) || "suggestion");
  const [message, setMessage] = useState(draftValues.message);
  const [email, setEmail] = useState(draftValues.email);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [pathname, setPathname] = useState("");
  
  useEffect(() => {
    setPathname(window.location.pathname); // eslint-disable-line react-hooks/set-state-in-effect -- sync pathname from browser on widget open
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      setErrorMessage("Please enter your feedback");
      return;
    }

    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch("/api/quick-feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: feedbackType,
          message: message.trim(),
          email: email.trim() || undefined,
          pageUrl: pathname,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus("success");
        clearDraft();
        setTimeout(() => {
          setIsOpen(false);
          setStatus("idle");
          setMessage("");
          setEmail("");
          setFeedbackType("suggestion");
        }, 2000);
      } else {
        setStatus("error");
        setErrorMessage(data.message || "Something went wrong");
        saveNow();
      }
    } catch {
      setStatus("error");
      setErrorMessage("Failed to submit feedback. Please try again.");
      saveNow();
    }
  };

  const handleClearForm = () => {
    setMessage("");
    setEmail("");
    setFeedbackType("suggestion");
    clearDraft();
  };

  const handleClose = () => {
    if (status !== "loading") {
      setIsOpen(false);
      setStatus("idle");
      setErrorMessage("");
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-(--purple) text-white rounded-full shadow-lg hover:bg-(--purple)/90 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-(--brand) focus:ring-offset-2"
        aria-label="Open feedback form"
      >
        <ChatIcon className="w-5 h-5" />
        <span className="hidden sm:inline font-medium">Feedback</span>
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-title"
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
            aria-hidden="true"
          />

          <div className="relative w-full max-w-md bg-gradient-purple rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-(--brand)/20">
              <div className="flex items-center justify-between">
                <h2 id="feedback-title" className="text-xl font-['Font-Bold'] text-(--gold)">
                  Quick Feedback
                </h2>
                <button
                  onClick={handleClose}
                  className="p-2 text-(--white)/70 hover:text-(--white) transition-colors rounded-full hover:bg-(--white)/10"
                  aria-label="Close feedback form"
                  disabled={status === "loading"}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">

              {status === "success" ? (
                <div className="py-8 text-center">
                  <div className="text-5xl mb-4">🎉</div>
                  <p className="text-lg font-['Font-Bold'] text-(--gold)">Thank you!</p>
                  <p className="text-(--white)/80">Your feedback helps us improve.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {hasDraft && lastSaved && (
                    <div className="flex items-center justify-between text-xs text-(--white)/60 p-2.5 bg-(--white)/5 rounded-lg border border-(--white)/10">
                      <span>Draft saved at {lastSaved.toLocaleTimeString()}</span>
                      <button
                        type="button"
                        onClick={handleClearForm}
                        className="text-(--gold) hover:underline font-medium"
                      >
                        Clear form
                      </button>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-(--white)/80 mb-2">
                      Type
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {feedbackTypes.map(({ value, label, emoji }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => { setFeedbackType(value); updateDraft("feedbackType", value); }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            feedbackType === value
                              ? "bg-(--brand) text-(--dark-purple)"
                              : "bg-(--white)/10 text-(--white)/80 hover:bg-(--white)/20"
                          }`}
                        >
                          <span className="mr-1">{emoji}</span> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="feedback-message" className="block text-sm font-medium text-(--white)/80 mb-2">
                      Your Feedback
                    </label>
                    <textarea
                      id="feedback-message"
                      value={message}
                      onChange={(e) => { setMessage(e.target.value); updateDraft("message", e.target.value); }}
                      placeholder="Tell us what you think..."
                      rows={4}
                      maxLength={1000}
                      className="w-full px-4 py-3 bg-(--dark-purple)/50 border border-(--brand)/30 rounded-lg text-(--white) placeholder:text-(--white)/50 focus:outline-none focus:ring-2 focus:ring-(--gold) resize-none"
                      disabled={status === "loading"}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="feedback-email" className="block text-sm font-medium text-(--white)/80 mb-2">
                      Email <span className="text-(--white)/50">(optional)</span>
                    </label>
                    <input
                      type="email"
                      id="feedback-email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); updateDraft("email", e.target.value); }}
                      placeholder="your@email.com"
                      maxLength={255}
                      autoComplete="email"
                      className="w-full px-4 py-3 bg-(--dark-purple)/50 border border-(--brand)/30 rounded-lg text-(--white) placeholder:text-(--white)/50 focus:outline-none focus:ring-2 focus:ring-(--gold)"
                      disabled={status === "loading"}
                    />
                    <p className="mt-1 text-xs text-(--white)/50">
                      For follow-up if needed
                    </p>
                  </div>

                  {errorMessage && (
                    <p className="text-sm text-red-300 bg-red-500/20 px-3 py-2 rounded-lg">{errorMessage}</p>
                  )}

                  <div className="text-xs text-(--white)/40">
                    Page: {pathname}
                  </div>

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full py-3 bg-(--brand) text-(--dark-purple) font-['Font-Bold'] rounded-full hover:brightness-110 transition-all hover:scale-[1.02] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === "loading" ? "Sending..." : "Send Feedback"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
